import { api } from '../../services/api';

export interface QuickAction {
    id: string;
    label: string;
    icon: string;
    description: string;
    actionType: 'route' | 'api_call';
    target: string;
    category: 'ess' | 'manager' | 'admin';
}

export interface DashboardStats {
    totalEmployees: number;
    totalEmployeesTrend: string;
    pendingLeaves: number;
    pendingLeavesTrend: string;
    departmentCount: number;
    departmentCountTrend: string;
    attendanceRate: string;
    attendanceRateTrend: string;
    totalSalaryExpense: number;
}

export interface DashboardResponse {
    stats: DashboardStats;
    charts: {
        headcountTrend: { month: string; count: number }[];
        employeesByDepartment: any[];
        leavesByType: any[];
        attendanceTrend: any[];
    };
}

export interface DashboardTask {
    id: string;
    type: 'LEAVE' | 'ADVANCE' | 'APPROVAL' | 'ACTION_REQUIRED' | 'INFO';
    title: string;
    description: string;
    requester: {
        firstName: string;
        lastName: string;
        initials: string;
    };
    overdueDays: number;
    createdAt: string;
    metadata?: any;
}

export interface DashboardEvent {
    id: string;
    title: string;
    type: 'BIRTHDAY' | 'HOLIDAY' | 'LEAVE' | 'EVENT' | 'Birthday' | 'Anniversary'; // Keeping old types for compat if needed
    date: string;
}

export interface DashboardActivity {
    id: string;
    user: string;
    action: string;
    time: string;
    avatar: string;
}

export const dashboardApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getDashboardStats: builder.query<DashboardResponse, void>({
            query: () => ({
                url: '/v1/dashboard/executive',
                method: 'GET',
            }),
            transformResponse: (response: any) => response.data,
            providesTags: ['Attendance', 'Leave', 'Employee'],
        }),
        getDashboardTasks: builder.query<DashboardTask[], void>({
            query: () => ({
                url: '/v1/dashboard/tasks',
                method: 'GET',
            }),
            transformResponse: (response: any) => response.data,
            providesTags: ['Leave', 'Payroll', 'Employee'],
        }),
        getDashboardEvents: builder.query<DashboardEvent[], void>({
            query: () => ({
                url: '/v1/dashboard/events',
                method: 'GET',
            }),
            transformResponse: (response: any) => response.data,
            providesTags: ['Employee'],
        }),
        getDashboardActivities: builder.query<DashboardActivity[], void>({
            query: () => ({
                url: '/v1/dashboard/activities',
                method: 'GET',
            }),
            transformResponse: (response: any) => response.data,
            providesTags: ['Employee', 'Leave', 'Attendance'],
        }),
        getQuickActions: builder.query<QuickAction[], void>({
            query: () => ({
                url: '/v1/dashboard/quick-actions',
                method: 'GET',
            }),
            transformResponse: (response: any) => response.data,
            providesTags: ['Attendance', 'User'],
        }),
        sendSupportQuery: builder.mutation<void, { subject: string; message: string }>({
            query: (data) => ({
                url: '/v1/dashboard/support',
                method: 'POST',
                body: data,
            }),
        }),
    }),
});

export const { 
    useGetDashboardStatsQuery, 
    useGetDashboardTasksQuery,
    useGetDashboardEventsQuery,
    useGetDashboardActivitiesQuery,
    useGetQuickActionsQuery,
    useSendSupportQueryMutation
} = dashboardApi;
